import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { EcardSendDeliveryMethod, EcardSendFormData, ToastVariants } from '@dotcom/crown-ui';
import { isMobile } from 'react-device-detect';
import { useAnalyticsContext } from '../../../context/analytics-context';
import { setIsToasterOpen, useAppContext } from '../../../context/app-context';
import { useCardContext } from '../../../context/card-context';
import { useInitializationDataContext } from '../../../context/data-context';
import { DigitalAddress } from '../../../global-types';
import { useFeatureFlags } from '../../../hooks/use-feature-flags';
import { useShallow } from 'zustand/react/shallow';
import { useAddressStore } from '../../../stores/address';
import { finalizeOrder } from '../../preview/utils/finalize-order';
import { PreparedEcardRecipient, prepareEcardRecipient } from '../utils/prepare-ecard-recipient';
import { getEcardOrderPreviewUrl } from '../utils/get-ecard-order-preview-url';

const SHARE_MESSAGE = 'A surprise from me and Hallmark';

/**
 * Coordinates recipient preparation, sharing, order finalization, and retry UI.
 * Successful Message share and order steps are retained so retries do not
 * repeat completed customer or backend actions.
 */
export const useEcardSend = () => {
  const { t } = useTranslation();
  const { appDispatch } = useAppContext();
  const { initializedDataState } = useInitializationDataContext();
  const {
    cardState: { coverPreviewUrl },
  } = useCardContext();
  const { IS_H_PLUS_ENABLED } = useFeatureFlags();
  const { trackSendEmailNow, trackSuccesfullySentEmail } = useAnalyticsContext();
  const projectId = initializedDataState.data?.project_id;
  const queryClient = useQueryClient();
  const { addressData, updateDigitalAddress } = useAddressStore(
    useShallow((state) => ({
      addressData: state.addressData,
      updateDigitalAddress: state.updateDigitalAddress,
    })),
  );
  const digitalAddress = addressData.digital as DigitalAddress | null;
  const [deliveryMethod, setDeliveryMethod] = useState<EcardSendDeliveryMethod>('email');
  const [messageRecipient, setMessageRecipient] = useState<PreparedEcardRecipient>();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationPending, setConfirmationPending] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [sharingFailed, setSharingFailed] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const copying = useRef(false);
  const busy = useRef(false);
  const mounted = useRef(true);
  const currentProjectId = useRef(projectId);
  currentProjectId.current = projectId;
  const emailRecipient = useRef<{ key: string; recipient: PreparedEcardRecipient }>();
  const pendingRecipient = useRef<PreparedEcardRecipient>();
  const messageShared = useRef(false);
  const messageCopied = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const isCurrent = (id: string | undefined) => mounted.current && currentProjectId.current === id;
  const getOrderPreviewUrl = () =>
    getEcardOrderPreviewUrl(
      coverPreviewUrl,
      initializedDataState.data?.variables?.template_data?.Faces?.[0]?.BackgroundUrl,
    );
  const showError = () =>
    setIsToasterOpen(appDispatch, {
      title: t('systemSendEmailNowError.title'),
      children: t('systemSendEmailNowError.description'),
      variant: ToastVariants.Error,
    });

  const preparation = useMutation({
    retry: false,
    mutationFn: async () => {
      try {
        const recipient = await prepareEcardRecipient(projectId ?? '', queryClient, undefined, {
          senderFirstName: digitalAddress?.sender_first_name ?? '',
          senderLastName: digitalAddress?.sender_last_name ?? '',
        });
        if (isCurrent(projectId)) {
          setMessageRecipient(recipient);
          messageShared.current = false;
          messageCopied.current = false;
        }
      } catch (error) {
        if (isCurrent(projectId)) {
          showError();
        }
        throw error;
      } finally {
        busy.current = false;
      }
    },
  });
  const prepareMessage = () => {
    if (busy.current || pendingRecipient.current) return;
    busy.current = true;
    preparation.mutate();
  };

  const changeDeliveryMethod = (method: EcardSendDeliveryMethod) => {
    if (busy.current || pendingRecipient.current) return;
    setDeliveryMethod(method);
    if (method === 'message' && (!messageRecipient || messageRecipient.projectId !== projectId)) {
      void prepareMessage();
    }
  };

  const finalize = async (recipient: PreparedEcardRecipient) => {
    if (!initializedDataState.data || recipient.projectId !== projectId) {
      throw new Error('Initialized project is unavailable');
    }
    const address = recipient.address;
    updateDigitalAddress(address);
    const finalizationState = {
      ...initializedDataState,
      addressData: { ...addressData, digital: address },
    };
    // The caller displays one retry toast for both classified and transport failures.
    let classifiedError = false;
    const markError = () => {
      classifiedError = true;
    };
    await finalizeOrder({
      initializedDataState: finalizationState,
      btlyUrl: address.url,
      cardPreviewImages: [
        {
          photoZoneTemplate: '',
          type: 'Front',
          dimensions: { width: 0, height: 0 },
          faceNumber: 1,
          url: getOrderPreviewUrl(),
        },
      ],
      recipientTypeCode: address.recipient_type_code === 'L' ? 'L' : 'E',
      submission: recipient.submission,
      queryClient,
      isHPlusEnabled: IS_H_PLUS_ENABLED,
      onNoHPlusNonSubscriberError: markError,
      onCrMemberLimitRechedError: markError,
      onNonCrMemberError: markError,
      onDefaultError: markError,
    });
    if (classifiedError) throw new Error('Digital order was not finalized');
    if (isCurrent(recipient.projectId)) {
      if (address.recipient_type_code === 'E') trackSuccesfullySentEmail();
      pendingRecipient.current = undefined;
      setConfirmationPending(false);
      if (address.recipient_type_code === 'L' && messageCopied.current) showLinkCopied();
      setShowConfirmation(true);
    }
  };

  const handleFailure = (recipient?: PreparedEcardRecipient) => {
    if (recipient && (recipient.submission.orderReferenceId || messageShared.current)) {
      pendingRecipient.current = recipient;
      setConfirmationPending(true);
    }
    showError();
  };

  const showLinkCopied = () => {
    if (isCurrent(projectId)) {
      setIsToasterOpen(appDispatch, {
        title: t('ecardSendView.linkCopiedTitle'),
        children: t('ecardSendView.linkCopiedDescription'),
        variant: ToastVariants.Success,
      });
    }
  };

  const copyRecipientLink = async (url: string) => {
    if (!url || !navigator.clipboard?.writeText) throw new Error('Clipboard or eCard link is unavailable');
    await navigator.clipboard.writeText(url);
  };

  const copyLinkAgain = async () => {
    if (copying.current) return;
    copying.current = true;
    setIsCopying(true);
    try {
      await copyRecipientLink(messageRecipient?.address.url ?? '');
      showLinkCopied();
    } catch {
      if (isCurrent(projectId)) {
        setIsToasterOpen(appDispatch, {
          title: t('ecardSendView.copyErrorTitle'),
          children: t('ecardSendView.copyErrorDescription'),
          variant: ToastVariants.Error,
        });
      }
    } finally {
      copying.current = false;
      if (mounted.current) setIsCopying(false);
    }
  };

  const emailSend = useMutation({
    retry: false,
    mutationFn: async (form: EcardSendFormData) => {
      let recipient: PreparedEcardRecipient | undefined;
      try {
        getOrderPreviewUrl();
        trackSendEmailNow(t('previewView.sendEmailNow'));
        const key = JSON.stringify([projectId, form]);
        recipient =
          emailRecipient.current?.key === key
            ? emailRecipient.current.recipient
            : await prepareEcardRecipient(projectId ?? '', queryClient, form);
        if (!isCurrent(projectId)) return;
        emailRecipient.current = { key, recipient };
        await finalize(recipient);
      } catch (error) {
        if (isCurrent(projectId)) handleFailure(recipient);
        throw error;
      } finally {
        busy.current = false;
      }
    },
  });
  const sendEmail = (form: EcardSendFormData) => {
    if (busy.current || pendingRecipient.current || showConfirmation) return;
    busy.current = true;
    emailSend.mutate(form);
  };

  const messageFinalization = useMutation({
    retry: false,
    mutationFn: finalize,
  });
  const sendMessage = async () => {
    if (busy.current || pendingRecipient.current || showConfirmation) return;
    busy.current = true;
    setIsSharing(true);
    setSharingFailed(false);
    messageFinalization.reset();
    confirmationRetry.reset();
    let nativeSharing = false;
    try {
      getOrderPreviewUrl();
      if (!messageRecipient || messageRecipient.projectId !== projectId) {
        throw new Error('The eCard link is not ready');
      }
      if (!messageShared.current) {
        if (isMobile && typeof navigator.share === 'function') {
          nativeSharing = true;
          // No awaited backend work before this call: native sharing requires the click's user activation.
          await navigator.share({ text: SHARE_MESSAGE, url: messageRecipient.address.url });
        } else {
          await copyRecipientLink(messageRecipient.address.url);
          messageCopied.current = true;
        }
        messageShared.current = true;
        nativeSharing = false;
      }
      if (mounted.current) setIsSharing(false);
      if (isCurrent(projectId)) await messageFinalization.mutateAsync(messageRecipient);
    } catch (error) {
      if (nativeSharing && error instanceof Error && error.name === 'AbortError') return;
      if (isCurrent(projectId)) {
        if (!messageShared.current) setSharingFailed(true);
        handleFailure(messageRecipient);
      }
    } finally {
      busy.current = false;
      if (mounted.current) setIsSharing(false);
    }
  };

  const confirmationRetry = useMutation({
    retry: false,
    mutationFn: async () => {
      try {
        const recipient = pendingRecipient.current;
        if (recipient) await finalize(recipient);
      } catch (error) {
        if (isCurrent(projectId)) handleFailure(pendingRecipient.current);
        throw error;
      } finally {
        busy.current = false;
      }
    },
  });
  const retryConfirmation = () => {
    if (busy.current || !pendingRecipient.current) return;
    busy.current = true;
    confirmationRetry.mutate();
  };

  return {
    deliveryMethod,
    changeDeliveryMethod,
    shareUrl: messageRecipient?.projectId === projectId ? messageRecipient?.address.url ?? '' : '',
    isBusy:
      preparation.isPending ||
      emailSend.isPending ||
      isSharing ||
      messageFinalization.isPending ||
      confirmationRetry.isPending,
    preparationFailed: preparation.isError,
    prepareMessage,
    sendEmail,
    sendMessage,
    showConfirmation,
    confirmationPending,
    retryConfirmation,
    isSendingMessage:
      isSharing || messageFinalization.isPending || (deliveryMethod === 'message' && confirmationRetry.isPending),
    messageSendFailed:
      deliveryMethod === 'message' &&
      (sharingFailed || (confirmationRetry.isIdle ? messageFinalization.isError : confirmationRetry.isError)),
    copyLinkAgain,
    isCopying,
  };
};
